// COSMETIC TATTOOING, THE THIRD SERVICE (TW-0279) — what a cosmetic signing IS on the form, in one place.
//
// docs/COSMETIC.md is the design and its decisions are settled. The API learned the service first (readers before
// writers: services/waiver-api/consolidated_api/cosmetic.py, slice 1), and THIS FILE IS THE FORM'S HALF OF THE SAME
// LISTS: the procedures a client picks from, what the visit is, which questions and clauses a procedure brings with it,
// and what the shop's state bars. The API validates and prints what the form posts and asks nothing itself, so the two
// files must say the same thing byte for byte; the API's own tests read this file and hold it to cosmetic.py
// (test_cosmetic_shop.TheFormPostsWhatTheApiKnows), and a procedure the API does not know is refused in a state that
// bars some (Georgia), so a single letter of drift there is a signing that cannot go through.
//
// The words a client reads for the clauses are service.ts's, beside the tattoo and the piercing ones; the Spanish of the
// clauses is the ONE root file, consent-sentences-es.json. Every Spanish line here awaits a native read, like every
// Spanish line on the form.
import type { Lang } from "./i18n";
// TW-0505 — the live tables (stateRules.ts RULES), filled in place when the shop's state's file lands: until then a state's
// cosmetic entry is absent, and every reader below answers as it answers for a state the contract does not carry
import { RULES as contract } from "./stateRules";

/** One procedure on the picker. `value` is what the form POSTS in `cosmeticProcedures` and what the signed waiver prints,
 *  English whatever language the client read; `es` is only what the Spanish list shows. Byte for byte cosmetic.py's
 *  PROCEDURES, "ombré" in NFC (one code point for the é): Georgia's bar compares the exact string. */
export type CosmeticProcedure = { value: string; es: string };

// THE PROCEDURES A CLIENT PICKS FROM, ONE OR MORE PER VISIT (the content read's proposal, section A, all 11). "Other" is
// posted as "Other" and the client says what in the description box. One entry per line: the API's test reads them.
export const COSMETIC_PROCEDURES: CosmeticProcedure[] = [
  { value: "Eyebrows: microblading (hair strokes)", es: "Cejas: microblading (trazos de pelo)" },
  { value: "Eyebrows: powder, ombré, or combination brows", es: "Cejas: polvo, ombré o combinadas" },
  { value: "Lips: lip blush or full lip color", es: "Labios: rubor de labios o color completo" },
  { value: "Lips: lip liner", es: "Labios: delineado de labios" },
  { value: "Eyeliner: upper, lower, or lash line", es: "Delineado de ojos: arriba, abajo o en la línea de las pestañas" },
  { value: "Areola and nipple", es: "Areola y pezón" },
  { value: "Scalp micropigmentation", es: "Micropigmentación del cuero cabelludo" },
  { value: "Scar, stretch-mark, or vitiligo camouflage", es: "Camuflaje de cicatrices, estrías o vitíligo" },
  { value: "Beauty mark or freckles", es: "Lunar o pecas" },
  { value: "Correction or cover of earlier cosmetic tattoo work", es: "Corrección o cobertura de un trabajo cosmético anterior" },
  { value: "Other", es: "Otro" },
];

export const COSMETIC_PROCEDURE_VALUES: string[] = COSMETIC_PROCEDURES.map((p) => p.value);

/** "THIS VISIT IS" (the proposal, after section A): posted as `cosmeticVisit`, the English value, printed as posted.
 *  New Jersey's 180-day touch-up rule (N.J.A.C. 8:27-8.4(a)2) is met by the form making a full record every visit. */
export const COSMETIC_VISITS: CosmeticProcedure[] = [
  { value: "First session", es: "Primera sesión" },
  { value: "Perfecting visit for work done here", es: "Retoque de perfeccionamiento de un trabajo hecho aquí" },
  { value: "Color refresh for work done here", es: "Refuerzo de color de un trabajo hecho aquí" },
  { value: "Work done somewhere else", es: "Un trabajo hecho en otro lugar" },
];

export const COSMETIC_VISIT_VALUES: string[] = COSMETIC_VISITS.map((v) => v.value);

/** A procedure or a visit in the client's language; the value itself is what is posted. */
export const cosmeticLabel = (option: CosmeticProcedure, lang: Lang = "en"): string => (lang === "es" ? option.es : option.value);

// WHICH PROCEDURES ARE WHICH, by the area they work on: what decides the questions and the clauses a procedure brings with
// it. cosmetic.py's groups, the same values.
// UNPLACED (TW-0279 slice 9): the procedures whose place the picker does not name, which can be anywhere, the face included,
// so a state whose rule is about a place asks where they are (the face question, `barredArea`).
export type CosmeticGroup = "BROWS" | "MICROBLADING" | "LIPS" | "EYELINER" | "AREOLA" | "SCALP" | "CAMOUFLAGE" | "UNPLACED";
export const COSMETIC_GROUPS: Record<CosmeticGroup, string[]> = {
  BROWS: ["Eyebrows: microblading (hair strokes)", "Eyebrows: powder, ombré, or combination brows"],
  MICROBLADING: ["Eyebrows: microblading (hair strokes)"],
  LIPS: ["Lips: lip blush or full lip color", "Lips: lip liner"],
  EYELINER: ["Eyeliner: upper, lower, or lash line"],
  AREOLA: ["Areola and nipple"],
  SCALP: ["Scalp micropigmentation"],
  CAMOUFLAGE: ["Scar, stretch-mark, or vitiligo camouflage"],
  UNPLACED: ["Scar, stretch-mark, or vitiligo camouflage", "Beauty mark or freckles", "Correction or cover of earlier cosmetic tattoo work", "Other"],
};

/** Does this list of picked procedures name any of `groups`? */
const namesAny = (picked: readonly string[], groups: readonly CosmeticGroup[]): boolean =>
  groups.some((group) => COSMETIC_GROUPS[group].some((value) => picked.includes(value)));

// ── the health questions ─────────────────────────────────────────────────────────────────────────────────────────

export type CosmeticQuestionKey =
  | "barredArea"
  | "pregnant"
  | "isotretinoin"
  | "diabetes"
  | "immuneTreatment"
  | "autoimmune"
  | "keloidScarring"
  | "earlierWork"
  | "botoxOrFiller"
  | "peelOrLaser"
  | "coldSores"
  | "herpesAtSite"
  | "contactsOrLashes"
  | "eyeCondition"
  | "surgeryOrRadiation";

export type CosmeticDetails =
  | "isotretinoinDetails"
  | "diabetesDetails"
  | "immuneTreatmentDetails"
  | "autoimmuneDetails"
  | "keloidScarringDetails"
  | "earlierWorkDetails"
  | "botoxOrFillerDetails"
  | "peelOrLaserDetails"
  | "coldSoresDetails"
  | "contactsOrLashesDetails"
  | "eyeConditionDetails"
  | "surgeryOrRadiationDetails";

export type CosmeticQuestion = {
  /** the key on form.questionnaire, cosmetic.py's */
  name: CosmeticQuestionKey;
  /** the box a yes opens, posted beside the questionnaire; none on a state's own question (COSMETIC_STATE_QUESTIONS), whose
   *  yes is refused in its rule's words, or, California's, recorded as its rule asks and nothing more */
  details?: CosmeticDetails;
  label: string;
  detailsLabel?: string;
  labelEs: string;
  detailsLabelEs?: string;
  /** TW-0279 slice 9 — a question whose words are the shop's state's own (COSMETIC_WORDED_BY_STATE: the face question):
   *  cosmeticQuestionsFor hands it back in its record's words, and the label here is never shown */
  wordsFromState?: boolean;
  /** TW-0279 slice 9 — the rule's sentence under the question, both languages, from the state's record (`asks[].why`):
   *  set by cosmeticQuestionsFor on a question a state asks for the record (California's herpes question) */
  why?: { en: string; es: string };
};

/** A COSMETIC TATTOO'S OWN HEALTH QUESTIONS (the proposal, section B), in cosmetic.py's order, each a yes or no whose yes
 *  opens a box. The rest of section B reuses a question every cosmetic form already asks, and is never asked twice: B1 is
 *  the pregnancy question and B8 the skin question (every page's, in words that ask about the site), B3 the medications
 *  box, B4 the bleeding question and B10 the allergies question (the state's own, asked on every cosmetic form). B9 is the
 *  piercing's `keloidScarring`, in a cosmetic tattoo's words. The proposal's "On yes" column is what each box asks for.
 *
 *  THREE ARE A STATE'S OWN (COSMETIC_STATE_QUESTIONS), asked only where the shop's state's record names them, and none opens
 *  a box. New Jersey bars permanent cosmetics on a client who is pregnant (N.J.A.C. 8:27-8.4(a)6.i; TW-0279 slice 6), and
 *  the question every page asks, "Are you pregnant or nursing?", is optional and cannot tell the bar from nursing, so
 *  `pregnant` asks the bar's own question where its record refuses on it, and a yes is refused in the rule's words. The face
 *  question (`barredArea`, slice 9) asks whether the work is in the place the state's rule bars, Georgia's eye socket or
 *  South Carolina's head, face and neck, in its record's own words (the label here is a fallback no page shows), when the
 *  client picked a procedure the picker cannot place; a yes is refused in the rule's words. California's herpes question
 *  (`herpesAtSite`, slice 9) is its questionnaire's "history of herpes infection at the proposed procedure site" (Cal.
 *  Health & Safety Code § 119303(b)(2)), asked whatever is picked in the place of the lips' cold-sore question; the rule
 *  asks it and nothing more, so a yes is recorded and neither refused nor followed by a box. */
export const COSMETIC_QUESTIONS: CosmeticQuestion[] = [
  {
    name: "barredArea",
    wordsFromState: true,
    label: "Will any of today's work be in a place your state's rule does not allow?",
    labelEs: "¿Alguna parte del trabajo de hoy será en un lugar que la regla de tu estado no permite?",
  },
  {
    name: "pregnant",
    label: "Are you pregnant?",
    labelEs: "¿Estás embarazada?",
  },
  {
    name: "isotretinoin",
    details: "isotretinoinDetails",
    label: "In the past 12 months, have you taken isotretinoin (Accutane) or tretinoin pills?",
    detailsLabel: "If yes, which one, and when did you last take it?",
    labelEs: "En los últimos 12 meses, ¿has tomado isotretinoína (Accutane) o tretinoína en pastillas?",
    detailsLabelEs: "Si es así, ¿cuál, y cuándo la tomaste por última vez?",
  },
  {
    name: "diabetes",
    details: "diabetesDetails",
    label: "Do you have diabetes?",
    detailsLabel: "If yes, tell us more.",
    labelEs: "¿Tienes diabetes?",
    detailsLabelEs: "Si es así, cuéntanos más.",
  },
  {
    name: "immuneTreatment",
    details: "immuneTreatmentDetails",
    label: "Are you getting chemotherapy or radiation, or do you take a medicine that weakens your immune system?",
    detailsLabel: "If yes, which treatment or medicine?",
    labelEs: "¿Estás recibiendo quimioterapia o radiación, o tomas algún medicamento que debilita tu sistema inmunitario?",
    detailsLabelEs: "Si es así, ¿qué tratamiento o medicamento?",
  },
  {
    name: "autoimmune",
    details: "autoimmuneDetails",
    label: "Do you have an autoimmune condition, such as lupus, psoriasis, vitiligo or rheumatoid arthritis?",
    detailsLabel: "If yes, which condition?",
    labelEs: "¿Tienes alguna enfermedad autoinmune, como lupus, psoriasis, vitíligo o artritis reumatoide?",
    detailsLabelEs: "Si es así, ¿cuál?",
  },
  {
    name: "keloidScarring",
    details: "keloidScarringDetails",
    label: "Do you get raised or thick scars (keloids) when your skin heals?",
    detailsLabel: "If yes, where, and what happened?",
    labelEs: "¿Te salen cicatrices abultadas o gruesas (queloides) cuando sana tu piel?",
    detailsLabelEs: "Si es así, ¿dónde y qué pasó?",
  },
  {
    name: "earlierWork",
    details: "earlierWorkDetails",
    label: "Have you had permanent makeup, microblading or a tattoo in this area before, or had one removed there?",
    detailsLabel: "If yes, who did it, when, and was a laser used?",
    labelEs: "¿Te has hecho antes maquillaje permanente, microblading o un tatuaje en esta zona, o te quitaste uno ahí?",
    detailsLabelEs: "Si es así, ¿quién lo hizo, cuándo y se usó láser?",
  },
  {
    name: "botoxOrFiller",
    details: "botoxOrFillerDetails",
    label: "Have you had Botox or a filler near this area in the last month, or are you planning one?",
    detailsLabel: "If yes, what, and on what date?",
    labelEs: "¿Te has puesto Botox o un relleno cerca de esta zona en el último mes, o piensas ponértelo?",
    detailsLabelEs: "Si es así, ¿qué y en qué fecha?",
  },
  {
    name: "peelOrLaser",
    details: "peelOrLaserDetails",
    label:
      "In the last month, have you had a chemical peel, laser, microneedling or a facial on this area, or used a retinoid cream like Retin-A or retinol there?",
    detailsLabel: "If yes, what, and when?",
    labelEs:
      "En el último mes, ¿te has hecho un peeling químico, láser, microneedling o un facial en esta zona, o has usado ahí una crema con retinoides como Retin-A o retinol?",
    detailsLabelEs: "Si es así, ¿qué y cuándo?",
  },
  {
    name: "coldSores",
    details: "coldSoresDetails",
    label: "Have you ever had a cold sore or fever blister?",
    detailsLabel: "If yes, when was the last one?",
    labelEs: "¿Alguna vez has tenido un herpes labial (fuego) o una ampolla de fiebre?",
    detailsLabelEs: "Si es así, ¿cuándo fue el último?",
  },
  {
    name: "herpesAtSite",
    label: "Have you ever had a herpes infection, like a cold sore or fever blister, where you're getting this done?",
    labelEs: "¿Alguna vez has tenido una infección de herpes, como un herpes labial (fuego) o una ampolla de fiebre, donde te vas a hacer esto?",
  },
  {
    name: "contactsOrLashes",
    details: "contactsOrLashesDetails",
    label: "Do you wear contact lenses or eyelash extensions?",
    detailsLabel: "If yes, which?",
    labelEs: "¿Usas lentes de contacto o extensiones de pestañas?",
    detailsLabelEs: "Si es así, ¿cuáles?",
  },
  {
    name: "eyeCondition",
    details: "eyeConditionDetails",
    label:
      "Do you have glaucoma, dry eye or another eye condition, or use prescription eye drops or an eyelash growth serum (like Latisse)?",
    detailsLabel: "If yes, which?",
    labelEs:
      "¿Tienes glaucoma, ojo seco u otra afección de los ojos, o usas gotas para los ojos con receta o un sérum para el crecimiento de las pestañas (como Latisse)?",
    detailsLabelEs: "Si es así, ¿cuál?",
  },
  {
    name: "surgeryOrRadiation",
    details: "surgeryOrRadiationDetails",
    label: "Have you had surgery or radiation on this area?",
    detailsLabel:
      "If yes, the date of your last surgery and of your last radiation, and has your surgeon or doctor said it's healed and okay to tattoo?",
    labelEs: "¿Te han hecho una cirugía o radiación en esta zona?",
    detailsLabelEs:
      "Si es así, la fecha de tu última cirugía y de tu última radiación, y ¿tu cirujano o médico dijo que ya sanó y que se puede tatuar?",
  },
];

/** WHAT A PROCEDURE BRINGS WITH IT. The questions the proposal asks by procedure (B12 to B17) and the clauses it shows by
 *  procedure (C15 to C20), by group: cosmetic.py's QUESTIONS_BY_PROCEDURE and CLAUSES_BY_PROCEDURE, the same keys and the
 *  same groups (the API's test reads both files). Every question not named here is on every cosmetic form. */
export const QUESTIONS_BY_PROCEDURE: Partial<Record<CosmeticQuestionKey, CosmeticGroup[]>> = {
  barredArea: ["UNPLACED"],
  botoxOrFiller: ["BROWS", "LIPS", "EYELINER"],
  peelOrLaser: ["BROWS", "LIPS", "EYELINER", "CAMOUFLAGE"],
  coldSores: ["LIPS"],
  contactsOrLashes: ["EYELINER"],
  eyeCondition: ["EYELINER"],
  surgeryOrRadiation: ["AREOLA", "CAMOUFLAGE"],
};

export type CosmeticClauseByProcedure =
  | "lipsColdSore"
  | "eyelinerNearEye"
  | "areolaTreatedSkin"
  | "scalpSessions"
  | "camouflageMatch"
  | "microbladingFades";

export const CLAUSES_BY_PROCEDURE: Record<CosmeticClauseByProcedure, CosmeticGroup[]> = {
  lipsColdSore: ["LIPS"],
  eyelinerNearEye: ["EYELINER"],
  areolaTreatedSkin: ["AREOLA"],
  scalpSessions: ["SCALP"],
  camouflageMatch: ["CAMOUFLAGE"],
  microbladingFades: ["MICROBLADING"],
};

/** A STATE'S OWN CLAUSE (cosmetic.py STATE_CLAUSES): on no cosmetic form unless its state's record names it in its
 *  `lines`, in the place of the plain clause it replaces. Two today, each a state's own C9: Oregon's, and Michigan's
 *  (TW-0279 slice 8: no work on numbed skin unless a doctor prescribed the product, R 333.13109(5)). */
export const COSMETIC_STATE_CLAUSES: readonly string[] = ["numbingOregon", "numbingMichigan"];

/** A state's own line on a cosmetic record (the contract's `cosmetic[state].lines`, written in the state's record and
 *  held to its rails by scripts/state-forms.py): a `clause` the form carries there (Oregon's numbing sentence), or a
 *  `notice` the state's consent already shows a cosmetic signing (California's FDA sentence, through noticesFor), and the
 *  plain clause it `replaces`, which the cosmetic form there does not carry. Never a branch on a state's name: the form
 *  and the door (app.cosmetic_clauses_on_the_form) read the same entries, and the door refuses a body carrying any box
 *  the form at that shop does not have. */
export type CosmeticLine = { clause?: string; notice?: string; replaces?: string; cite?: string; citeUrl?: string };

/** The shop's state's own lines, [] for no state, an unknown one, or one with none. */
export function cosmeticLinesFor(state: string | null | undefined): CosmeticLine[] {
  const lines = cosmeticRuleFor(state)?.lines;
  return Array.isArray(lines) ? lines.filter((line): line is CosmeticLine => Boolean(line) && typeof line === "object") : [];
}

/** A STATE'S OWN QUESTION (cosmetic.py STATE_QUESTIONS): on no cosmetic form unless its state's record names it, in
 *  `cosmetic[state].asks` (California's herpes question, Georgia's and South Carolina's face question: TW-0279 slice 9) or,
 *  with no `asks` entry, in `cosmetic[state].refuses` (New Jersey's pregnancy bar, slice 6). */
export const COSMETIC_STATE_QUESTIONS: readonly string[] = ["barredArea", "pregnant", "herpesAtSite"];
/** …and the one whose words are the state's own (cosmetic.py WORDED_BY_STATE): its record's `asks` entry carries them. */
export const COSMETIC_WORDED_BY_STATE: readonly string[] = ["barredArea"];

/** A question of a state's own on a cosmetic signing (the contract's `cosmetic[state].asks`, written in the state's record and
 *  held to its rails by scripts/state-forms.py; TW-0279 slice 9): the question, the plain question it `replaces` there, the
 *  shop's own switch under which the rule does not reach the studio (`unless`), its words where they are the state's
 *  (`words`, and the signed copy's `label`), and the rule's sentence under it (`why`). */
export type CosmeticAsk = {
  question: string;
  replaces?: string;
  unless?: string;
  words?: { en: string; es: string };
  label?: string;
  why?: { en: string; es: string };
  cite?: string;
  citeUrl?: string;
};

/** The shop's state's own questions, [] for no state, an unknown one, or one with none. */
export function cosmeticAsksFor(state: string | null | undefined): CosmeticAsk[] {
  const asks = cosmeticRuleFor(state)?.asks;
  return Array.isArray(asks) ? asks.filter((ask): ask is CosmeticAsk => Boolean(ask) && typeof ask === "object") : [];
}

/** The cosmetic questions this visit asks, in the page's order: the ones on every cosmetic form, a procedure's own only
 *  when one of the picked procedures brings it, and a state's own only where the shop's state's record asks it (`asks`) or
 *  refuses on it (`refuses`), read off the shop's row, never the client's address. A plain question the state's own takes
 *  the place of (`replaces`: California's herpes question for the lips' cold sores) is not asked there, and a state's own
 *  is not asked while the shop's own switch its entry names is on (`unless`: South Carolina's physician-directed studio),
 *  a literal true only, the bars' reading. A question a state asks comes back in its words where they are the state's, with
 *  the rule's sentence under it where the record gives one. Nothing picked yet asks the every-form ones and the state's
 *  own that every procedure brings. The door asks the same (state_forms.cosmetic_rule_questions). */
export function cosmeticQuestionsFor(picked: readonly string[], state?: string | null, publicData?: unknown): CosmeticQuestion[] {
  const rule = cosmeticRuleFor(state);
  const row = (publicData ?? {}) as Record<string, unknown>;
  const lifted = (ask: CosmeticAsk) => Boolean(ask.unless && row[ask.unless] === true);
  const asks = new Map(cosmeticAsksFor(state).map((ask) => [ask.question, ask]));
  const refusedOn = new Set((rule?.refuses ?? []).map((bar) => bar.question));
  const replaced = new Set(Array.from(asks.values()).filter((ask) => !lifted(ask)).map((ask) => ask.replaces ?? ""));
  const out: CosmeticQuestion[] = [];
  for (const q of COSMETIC_QUESTIONS) {
    const ask = asks.get(q.name);
    if (COSMETIC_STATE_QUESTIONS.includes(q.name)) {
      if (ask ? lifted(ask) : !refusedOn.has(q.name)) continue;
    } else if (replaced.has(q.name)) {
      continue;
    }
    const groups = QUESTIONS_BY_PROCEDURE[q.name];
    if (groups && !namesAny(picked, groups)) continue;
    if (!ask) {
      out.push(q);
      continue;
    }
    const words = q.wordsFromState && ask.words ? { label: ask.words.en, labelEs: ask.words.es } : {};
    out.push({ ...q, ...words, ...(ask.why ? { why: ask.why } : {}) });
  }
  return out;
}

/** Is this by-procedure clause shown for these picks? */
export const clauseShownFor = (key: CosmeticClauseByProcedure, picked: readonly string[]): boolean =>
  namesAny(picked, CLAUSES_BY_PROCEDURE[key]);

/** The question, its box and the rule's sentence under it, in the client's language ("" for a box the question does not
 *  open, and for a sentence it does not carry). */
export const cosmeticQuestionWords = (q: CosmeticQuestion, lang: Lang = "en"): { label: string; detailsLabel: string; why: string } =>
  lang === "es"
    ? { label: q.labelEs, detailsLabel: q.detailsLabelEs ?? "", why: q.why?.es ?? "" }
    : { label: q.label, detailsLabel: q.detailsLabel ?? "", why: q.why?.en ?? "" };

// ── what the shop's state bars ───────────────────────────────────────────────────────────────────────────────────

/** A procedure a state's rule bars (the contract's `cosmetic[state].barred`, from the state's record): off the picker
 *  unless `unless` names a switch the shop turned on (a literal true on the row, as the API reads it). `lifted` (TW-0279
 *  slice 7) is the sentence the client reads under the picker while that switch lifts the bar, the rule's own words in both
 *  languages (South Carolina's physician-directed studio); a bar without it says nothing when lifted (Georgia's brows). */
export type CosmeticBar = {
  procedures: string[];
  unless?: string;
  cite?: string;
  citeUrl?: string;
  refusal: { en: string; es: string };
  lifted?: { en: string; es: string };
};

/** An answer a state's rule bars the work on (the contract's `cosmetic[state].refuses`): New Jersey's isotretinoin. */
export type CosmeticAnswerBar = {
  question: string;
  answer: string;
  cite?: string;
  citeUrl?: string;
  refusal: { en: string; es: string };
};

/** TW-0279 slice 10 — a state's rule that wants the client's health care provider's written instructions kept with the
 *  record (the contract's `cosmetic[state].providerInstructions`, from the state's record: New Jersey's N.J.A.C.
 *  8:27-4.4(a)). `why` is the sentence the client reads under the photo, in the rule's plain words, in both languages. */
export type ProviderInstructionsRule = {
  quote: string;
  cite: string;
  citeUrl: string;
  why: { en: string; es: string };
};

type CosmeticEntry = {
  follows?: string;
  minors?: boolean;
  barred?: CosmeticBar[];
  asks?: CosmeticAsk[];
  refuses?: CosmeticAnswerBar[];
  lines?: CosmeticLine[];
  providerInstructions?: ProviderInstructionsRule;
};
const COSMETIC = ((contract as unknown as { cosmetic?: Record<string, CosmeticEntry> }).cosmetic ?? {}) as Record<string, CosmeticEntry>;

/** The state's cosmetic entry, or nothing for no state and a state the contract does not carry. */
export function cosmeticRuleFor(state: string | null | undefined): CosmeticEntry | undefined {
  const abbr = `${state ?? ""}`.trim().toUpperCase();
  return abbr ? COSMETIC[abbr] : undefined;
}

/** The bars in force for THIS shop: its state's, less any whose switch the shop turned on (a literal true only, the way
 *  the API lifts it). Read off the shop's row, never the client's address: the studio's state is whose rule applies. */
export function cosmeticBarsFor(state: string | null | undefined, publicData: unknown): CosmeticBar[] {
  const row = (publicData ?? {}) as Record<string, unknown>;
  return (cosmeticRuleFor(state)?.barred ?? []).filter((bar) => !(bar.unless && row[bar.unless] === true));
}

/** TW-0279 slice 7 — the bars this shop's own switch LIFTS that have something to say about it: the state's entries whose
 *  `unless` is a literal true on the row and that carry `lifted` words, in the record's order. South Carolina's
 *  physician-directed studio is offered brows, lips, eyeliner and scalp work with the statute's and the medical board's
 *  sentence under the list; a bar in force, or a lifted one with no words, is not here. */
export function cosmeticLiftsFor(state: string | null | undefined, publicData: unknown): CosmeticBar[] {
  const row = (publicData ?? {}) as Record<string, unknown>;
  return (cosmeticRuleFor(state)?.barred ?? []).filter((bar) => Boolean(bar.unless && row[bar.unless] === true && bar.lifted));
}

/** The procedures this shop's picker offers: every one but those its state bars. A barred procedure is never on the
 *  page, so it can never be ticked; the posted list is filtered to this list again (service.ts submissionForm). */
export function cosmeticProceduresOffered(state: string | null | undefined, publicData: unknown): CosmeticProcedure[] {
  const barred = new Set(cosmeticBarsFor(state, publicData).flatMap((bar) => bar.procedures));
  return COSMETIC_PROCEDURES.filter((p) => !barred.has(p.value));
}

/** The picks this signing posts: the ones on the list it was offered, in the list's order, each once. */
export function postedProcedures(picked: unknown, offered: readonly string[] = COSMETIC_PROCEDURE_VALUES): string[] {
  const chosen = new Set(Array.isArray(picked) ? picked.filter((v): v is string => typeof v === "string") : []);
  return COSMETIC_PROCEDURE_VALUES.filter((value) => offered.includes(value) && chosen.has(value));
}

// ── the client's health care provider's written instructions (TW-0279 slice 10, 2026-10-08) ──────────────────────

/** WHICH WORK ASKS FOR THE PROVIDER'S WRITTEN INSTRUCTIONS (cosmetic.py PROVIDER_INSTRUCTIONS_GROUPS and _SCAR_GROUPS, the
 *  same names; the API's test reads both): areola and camouflage work always, and scalp work only when the client says it
 *  covers scars. New Jersey's rule: areola restoration and camouflage are done "in accordance with specific written
 *  instructions issued by the client's health care provider, which the practitioner shall maintain with the client's
 *  application" (N.J.A.C. 8:27-4.4(a)), and its camouflage includes "scarring that results from surgery or the application
 *  of hair transplants" (8:27-1.3). Scalp work for thinning hair alone is not camouflage, so it asks one question first. */
export const PROVIDER_INSTRUCTIONS_GROUPS: CosmeticGroup[] = ["AREOLA", "CAMOUFLAGE"];
export const PROVIDER_INSTRUCTIONS_SCAR_GROUPS: CosmeticGroup[] = ["SCALP"];
/** The shop's own switch (`public_data.ask_provider_instructions_photo`, a literal true), off until the owner turns it on. */
export const PROVIDER_INSTRUCTIONS_SWITCH = "ask_provider_instructions_photo";

/** The shop's state's rule naming the provider's written instructions, or null for no state, an unknown one, or one whose
 *  record names none (every state but New Jersey). Read off the shop's row, never the client's address. */
export function providerInstructionsRuleFor(state: string | null | undefined): ProviderInstructionsRule | null {
  const rule = cosmeticRuleFor(state)?.providerInstructions;
  return rule && typeof rule === "object" && rule.cite ? rule : null;
}

/** Does this shop's cosmetic form ask about the provider's written instructions at all: its state's rule names them
 *  (whatever the switch says), or its own switch is a literal true. The API reads the same two the same way
 *  (app.provider_instructions_asked). */
export function asksProviderInstructions(state: string | null | undefined, publicData: unknown): boolean {
  const row = (publicData ?? {}) as Record<string, unknown>;
  return Boolean(providerInstructionsRuleFor(state)) || row[PROVIDER_INSTRUCTIONS_SWITCH] === true;
}

/** Do these picks name scalp work, whose one question decides whether the photo is asked? */
export const scalpNamed = (picked: readonly string[]): boolean => namesAny(picked, PROVIDER_INSTRUCTIONS_SCAR_GROUPS);

/** Do these picks need the photo, where the form asks about it at all: areola or camouflage work, or scalp work the client
 *  said covers scars (`scalpScars` a literal true; null before they answer). cosmetic.py provider_instructions_needed. */
export function providerInstructionsNeeded(picked: readonly string[], scalpScars: boolean | null): boolean {
  return namesAny(picked, PROVIDER_INSTRUCTIONS_GROUPS) || (scalpScars === true && scalpNamed(picked));
}

/** The first answer this shop's state bars the work on, or null: refused at the question, before anything posts. The
 *  API refuses the same body (COSMETIC_ANSWER_REFUSED). `asked` (TW-0279 slice 9) is the questions this visit's page asks
 *  (cosmeticQuestionsFor): an answer left on the form's state from a question the page no longer asks (the face question,
 *  once its procedure is unticked, or under the shop's own switch) is never posted, so it refuses nothing. */
export function cosmeticAnswerRefusal(
  state: string | null | undefined,
  questionnaire: Partial<Record<string, unknown>> | null | undefined,
  asked?: readonly string[]
): CosmeticAnswerBar | null {
  for (const bar of cosmeticRuleFor(state)?.refuses ?? []) {
    if (asked && !asked.includes(bar.question)) continue;
    const answer = `${questionnaire?.[bar.question] ?? ""}`.trim().toLowerCase();
    if (answer && answer === `${bar.answer}`.trim().toLowerCase()) return bar;
  }
  return null;
}
